import { type ElementOrSelector, useAnimate } from 'motion/react';
import { type FunctionComponent, type SVGProps, useEffect } from 'react';
import { randomInt } from 'es-toolkit/math';

const playAnimation = async (element: ElementOrSelector, animate: ReturnType<typeof useAnimate>[1], fadingDurationMilliseconds: number) => {
  // initial state, instant
  await animate(
    element,
    {
      top: randomInt(80) + 10 + '%',
      left: randomInt(80) + 10 + '%',
      rotate: randomInt(360),
      opacity: 0,
    },
    { duration: 0 },
  );

  // actual animation
  await animate(
    [
      [element, { opacity: 1 }],
      [element, { opacity: 0 }],
    ],
    {
      defaultTransition: {
        duration: fadingDurationMilliseconds * 0.001, // milliseconds to seconds
        ease: 'easeInOut',
      },
    },
  );
};

const playAnimationLoop = (
  element: ElementOrSelector,
  animate: ReturnType<typeof useAnimate>[1],
  fadingDurationMilliseconds: number,
  delayBetweenLoopMilliseconds: number,
  delayOffset: number,
): number => {
  setTimeout(() => void playAnimation(element, animate, fadingDurationMilliseconds), delayOffset);

  return setInterval(
    () => void playAnimation(element, animate, fadingDurationMilliseconds),
    fadingDurationMilliseconds * 2 + // fade-in & fade-out duration
      delayBetweenLoopMilliseconds + // delay between loops
      delayOffset, // delay between each key
  );
};

export const useBackgroundAnimation = (
  svgElements: FunctionComponent<SVGProps<SVGSVGElement>>[],
  elementSelectorPrefix: string,
  fadingDurationMilliseconds: number,
  delayBetweenLoopMilliseconds: number,
) => {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const intervalIds = svgElements.map((_, index) =>
      playAnimationLoop(
        `.${elementSelectorPrefix}-${index}`,
        animate,
        fadingDurationMilliseconds,
        delayBetweenLoopMilliseconds,
        randomInt(500 * index, 500 * (index + 1)),
      ),
    );

    return () => intervalIds.forEach(clearInterval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return scope;
};
